import dayjs from 'dayjs';
import { ApolloClient } from '@apollo/client';
import { useLazyQuery, useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useTransition } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Download, ChevronRight } from 'lucide-react';

import { withSuspense } from 'src/hoc';
import { InvoiceStatusEnum } from 'src/graphql/schema';
import { InvoicePdfDocument, type IInvoicePdfQuery } from 'src/graphql/actions/invoices/invoicePdf.query';
import { InvoicesDocument } from 'src/graphql/actions/invoices/invoices.query';
import formatPrice from 'src/utils/currency-code-to-symbol';
import { Table, TableRow, TableBody, TableCell, TableHeader, TableHead, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

import { EmptyInvoices } from './ui';
import { labelStatusMap } from './model/invoices.enum';

type IInvoiceRow = {
  id: string;
  date: string;
  amount: string;
  status: InvoiceStatusEnum;
};

const columnHelper = createColumnHelper<IInvoiceRow>();
const columns = (
  getInvoicePdf: ({ variables }: { variables: { id: string } }) => Promise<ApolloClient.QueryResult<IInvoicePdfQuery>>,
) => [
  columnHelper.accessor('id', {
    cell: (props) => 'Invoice #' + props.getValue(),
  }),
  columnHelper.accessor('status', {
    cell: (props) => {
      const { text, bgColor, color } = labelStatusMap[props.getValue()];

      return <Badge className={cn(color, bgColor)}>{text}</Badge>;
    },
  }),
  columnHelper.accessor('date', {
    cell: (props) => dayjs(props.getValue()).format('MMM DD, YYYY'),
  }),
  columnHelper.accessor('amount', {
    cell: (props) => props.getValue(),
  }),
  columnHelper.accessor('id', {
    cell: (props) => {
      const handleDownload = async () => {
        const { data } = await getInvoicePdf({ variables: { id: props.getValue() } });
        window.location.href = data?.invoicePdf?.downloadUrl ?? '';
      };

      return (
        <Button onClick={handleDownload} variant="link" size="icon">
          <Download />
        </Button>
      );
    },
  }),
];

const BillingInvoicesContainer = () => {
  const [isPending, startTransition] = useTransition();
  const {
    data: { invoices },
    fetchMore,
  } = useSuspenseQuery(InvoicesDocument);
  const [getInvoicePdf, { loading: loadingPdf }] = useLazyQuery(InvoicePdfDocument);

  const onLoadMore = async () => {
    startTransition(() => {
      fetchMore({
        updateQuery: (previousResult, { fetchMoreResult }) => ({
          ...previousResult,
          invoices: {
            ...fetchMoreResult.invoices,
            nodes: [...previousResult.invoices.nodes, ...fetchMoreResult.invoices.nodes],
            nextOffset: fetchMoreResult.invoices.nextOffset,
          },
        }),
        variables: {
          offset: invoices.nextOffset,
        },
      });
    });
  };

  const invoicesData: IInvoiceRow[] = useMemo(
    () =>
      invoices.nodes.map((invoice) => ({
        id: invoice.id,
        date: invoice.date,
        amount: formatPrice(invoice.currencyCode, invoice.total, true),
        status: invoice.status,
      })),
    [invoices.nodes],
  );

  const table = useReactTable({
    data: invoicesData,
    columns: columns(getInvoicePdf),
    getCoreRowModel: getCoreRowModel(),
  });

  if (!invoices.nodes.length) return <EmptyInvoices />;

  return (
    <div className="border border-border rounded-md">
      <Table isLoading={loadingPdf}>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Billing date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {invoices.nextOffset && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5}>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={onLoadMore} isLoading={isPending}>
                    Load more <ChevronRight />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
};

const InvoicesSkeleton = () => (
  <div className="border border-border rounded-md p-4 flex flex-col gap-3">
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
  </div>
);

export default withSuspense(BillingInvoicesContainer, InvoicesSkeleton);
